import { Script } from 'gatsby'
import React from 'react'
import {
  PageBase,
  SanityHomePage,
  SanityPhoto,
} from '../../../types/generated/graphql-types'
import imageBuilder from '../../sanity/imageBuilder'
import { notEmpty, validImage } from '../../utilities/filters'
import { IS_EQT_MARKET } from '../../utilities/site'
import { isNullOrWhiteSpace } from '../../utilities/string'

export interface MetaTag {
  name: string
  content: string
}

export interface AlternateLinkItem {
  href: string
  hreflang: string
}

export type LanguageVersion = {
  language?: string
  path?: string
  urlFragments?: string[]
}

interface GetAlternateLinksOptions {
  basePagePath?: string
  languageVersions?: LanguageVersion[]
}

const getAlternateLinks = ({
  basePagePath,
  languageVersions,
}: GetAlternateLinksOptions): AlternateLinkItem[] => {
  const alternateLinks: AlternateLinkItem[] = []

  // Note: Don't check for empty strings for paths, since the base language home page has an empty path.
  if (basePagePath !== undefined) {
    alternateLinks.push({
      href: `${process.env.GATSBY_BASE_URL}${basePagePath}`,
      hreflang: 'x-default',
    })
  }

  if (languageVersions) {
    for (const languageVersion of languageVersions.filter(notEmpty)) {
      if (languageVersion.path !== undefined) {
        alternateLinks.push({
          href: `${process.env.GATSBY_BASE_URL}${languageVersion.path}`,
          hreflang: languageVersion.language ?? 'en',
        })
      }
    }
  }

  return alternateLinks
}

export interface SeoProps {
  basePage?: PageBase | null
  description?: string | null | undefined
  homePage?: SanityHomePage
  indexing?: string | null
  languageVersions?: LanguageVersion[]
  meta?: MetaTag[]
  ogImage?: SanityPhoto | null
  path?: string
  title?: string | null | undefined
  jsonLD?: Array<{ [key: string]: string }>
}

export const Seo = ({
  basePage,
  description,
  homePage,
  indexing,
  languageVersions,
  meta,
  ogImage,
  path,
  title,
  jsonLD,
}: SeoProps): JSX.Element => {
  let metaTags: MetaTag[] = []
  let ogImageAltText = ogImage?.altText ?? ogImage?.caption

  if (homePage) {
    if (isNullOrWhiteSpace(title)) {
      title = homePage.metaTitle
    }

    if (isNullOrWhiteSpace(description)) {
      description = homePage.metaDescription
    }

    if (!validImage(ogImage)) {
      ogImage = homePage.metaImage
    }

    if (isNullOrWhiteSpace(ogImageAltText)) {
      ogImageAltText = homePage.metaImage?.altText
    }
  }

  if (!isNullOrWhiteSpace(title)) {
    metaTags.push({
      name: 'og:title',
      content: title,
    })
  }

  if (!isNullOrWhiteSpace(description)) {
    metaTags.push({
      name: 'description',
      content: description,
    })

    metaTags.push({
      name: 'og:description',
      content: description,
    })
  }

  if (validImage(ogImage)) {
    metaTags.push({
      name: 'og:image',
      content:
        imageBuilder
          .image(ogImage)
          .width(1200)
          .height(630)
          .fit('clip')
          .auto('format')
          .url() ?? '',
    })
  }

  if (!isNullOrWhiteSpace(ogImageAltText)) {
    metaTags.push({
      name: 'og:image:alt',
      content: ogImageAltText,
    })
  }

  if (meta) {
    metaTags = metaTags.concat(meta)
  }

  // Note: Don't check for empty strings for paths, since the base language home page has an empty path.
  const basePagePath = basePage?.path ?? undefined

  // TODO: Correct canonical url for pages with paging, and people teams. Should probably be sourced from gatsby-node.
  const canonicalPath =
    basePage?.createMirroredPages && basePagePath !== undefined ? basePagePath : path

  const canonicalUrl =
    canonicalPath !== undefined
      ? `${process.env.GATSBY_BASE_URL}${canonicalPath}`
      : undefined

  const alternateLinks = getAlternateLinks({
    basePagePath,
    languageVersions,
  })

  return (
    <>
      {/* TODO: Turn on indexing when content is ready */}
      {IS_EQT_MARKET || process.env.GATSBY_SANITY_DATASET === 'eqt-odin-prod' ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content={indexing ?? 'index,follow'} />
      )}

      {!isNullOrWhiteSpace(canonicalUrl) && <link href={canonicalUrl} rel="canonical" />}

      {alternateLinks.map((link, key) => (
        <link href={link.href} hrefLang={link.hreflang} key={key} rel="alternate" />
      ))}

      {metaTags.map((tag, index) => (
        <meta key={index} {...tag} />
      ))}

      {jsonLD && (
        <Script type="application/ld+json" strategy="off-main-thread">
          {`{
          '@context': 'https://schema.org/',
          ${jsonLD.map(item => {
            const objectKey = Object.keys(item)[0]
            return `'${objectKey}' : '${item[objectKey]}'`
          })}
          }`}
        </Script>
      )}
    </>
  )
}
